import { useEffect, useState, useRef } from "react";

// @mui
import { Box, TextField, Typography } from "@mui/material";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";

// Utils
import { debounce } from "lodash";

// Styles
import { Theme } from "@mui/material/styles";
import { makeStyles, createStyles } from "@mui/styles";
import React from "react";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        employeesInputsRoot: {
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
        },
        employeesInputGroup: {
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
        },
        labelIcon: {
            fontSize: 16,
            marginRight: 4,
            color: theme.palette.common.black,
        },
        labelTitle: {
            fontSize: 14,
            fontWeight: 500,
            color: theme.palette.primary.main,
        },
        employeesMaxCount: {
            marginLeft: theme.spacing(1),
        },
    })
);

export type MinimumMaximum = { minimum: number | string; maximum: number | string };

const EmployeesMinMax = ({
    clearFilters,
    values,
    setValues,
}: {
    clearFilters: number;
    values: MinimumMaximum;
    setValues: React.Dispatch<React.SetStateAction<MinimumMaximum>>;
}) => {
    const classes = useStyles();
    const [innerValues, setInnerValues] = useState<{ minimum: number | string; maximum: number | string }>({
        minimum: values.minimum ?? "",
        maximum: values.maximum ?? "",
    });

    const delayedOnChange = useRef(
        debounce((newValues: MinimumMaximum) => {
            setValues(newValues);
        }, 500)
    ).current;

    const onChange = (from: "minimum" | "maximum", value: string) => {
        const newValue = Number(value);
        if (value !== "" && !Number.isNaN(newValue) && newValue < 0) {
            return;
        }
        const inputValue = value === "" || Number.isNaN(newValue) ? "" : newValue;
        const newValues = { ...innerValues, [from]: inputValue };

        setInnerValues(newValues);
    };

    const onBlur = (from: "minimum" | "maximum") => {
        const minimum = innerValues.minimum === "" ? NaN : Number(innerValues.minimum);
        const maximum = innerValues.maximum === "" ? NaN : Number(innerValues.maximum);

        if (Number.isNaN(minimum) || Number.isNaN(maximum)) {
            return;
        }

        if (from === "minimum" && minimum > maximum) {
            const normalized = { ...innerValues, minimum: maximum };
            setInnerValues(normalized);
            delayedOnChange(normalized);
            return;
        }

        if (from === "maximum" && maximum < minimum) {
            const normalized = { ...innerValues, maximum: minimum };
            setInnerValues(normalized);
            delayedOnChange(normalized);
            return;
        }

        delayedOnChange(innerValues);
    };

    useEffect(() => {
        if (clearFilters > 0) {
            setInnerValues({ minimum: "", maximum: "" });
        }
    }, [clearFilters]);

    useEffect(() => () => delayedOnChange.cancel(), [delayedOnChange]);

    return (
        <Box className={classes.employeesInputsRoot}>
            <Box display="flex">
                <SupervisorAccountIcon className={classes.labelIcon} />
                <Typography className={classes.labelTitle}>Employees count</Typography>
            </Box>
            <Box className={classes.employeesInputGroup}>
                <TextField
                    id="employees-count-min"
                    type="number"
                    size="small"
                    placeholder="Min:"
                    value={innerValues.minimum}
                    inputProps={{ min: 0 }}
                    onChange={(ev) => onChange("minimum", ev.target.value)}
                    onBlur={() => onBlur("minimum")}
                />
                <TextField
                    id="employees-count-max"
                    type="number"
                    size="small"
                    classes={{ root: classes.employeesMaxCount }}
                    placeholder="Max:"
                    value={innerValues.maximum}
                    inputProps={{ min: 0 }}
                    onChange={(ev) => onChange("maximum", ev.target.value)}
                    onBlur={() => onBlur("maximum")}
                />
            </Box>
        </Box>
    );
};

export default EmployeesMinMax;
